import { isPlatformServer } from '@angular/common';
import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID, REQUEST } from '@angular/core';

import { ENotificationCardType } from '@rpgc-ai/shared-interfaces/interfaces/notifications.interfaces';

import { environment } from '@rpgc-ai/environments';

import { catchError, throwError } from 'rxjs';

import { SessionService } from './session.service';

import { NotificationsService } from '../notifications/notifications.service';

/**
 * HTTP-интерцептор для добавления базового URL API к исходящим запросам
 * Токен авторизации передаётся через cookie автоматически в браузере
 * или добавляется вручную при SSR
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationsService = inject(NotificationsService);
  const sessionService = inject(SessionService);
  // Получаем базовый URL API из переменных окружения
  const apiUrl = environment.API_URL || '';
  const platformId = inject(PLATFORM_ID);
  const isProduction = environment.production;

  // Проверяем, является ли запрос относительным и не содержит ли он уже полный URL
  let updatedReq = req;
  if (!req.url.startsWith('http') && !req.url.startsWith('//')) {
    const url = `${apiUrl}${req.url.startsWith('/') ? '' : '/'}${req.url}`;
    updatedReq = req.clone({ url });
  }

  // Если запрос выполняется на сервере (SSR), нужно вручную добавить cookie
  if (isPlatformServer(platformId)) {
    const request = inject(REQUEST, { optional: true });
    if (request) {
      const cookieHeader = request.headers?.get('cookie');

      const appDomain = isProduction ? environment.APP_DOMAIN || 'cortex.caichat.ai' : 'cortex.caichat.ai';

      if (cookieHeader) {
        updatedReq = updatedReq.clone({
          headers: updatedReq.headers.set('Cookie', cookieHeader),
        });

        // Также добавляем заголовок хоста, чтобы сервер знал, от имени какого домена идет запрос
        updatedReq = updatedReq.clone({
          headers: updatedReq.headers.set('Host', appDomain),
        });
      }
    }
  }

  // В браузере cookie с токеном будет автоматически отправлен браузером

  // Добавляем заголовок withCredentials: true, чтобы браузер отправлял cookie даже при CORS
  updatedReq = updatedReq.clone({
    withCredentials: true,
  });

  return next(updatedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Обработка ошибки авторизации
      if (error.status === 401 && error.error.code !== 'INVALID_TWO_FACTOR_COOKIE') {
        // Не используем console.log для соблюдения правил линтера
        sessionService.client.signOut();
        sessionService.clearSession({ notifyOtherTabs: true, redirectToHome: true });

        // Определяем сообщение для отображения
        let errorMessage = 'Ошибка авторизации: неверный логин или пароль';

        // Пытаемся извлечь сообщение из ответа сервера в разных форматах
        if (error.error) {
          if (typeof error.error === 'object') {
            // Вариант 1: Формат с полем message
            if (error.error.message) {
              errorMessage = `Ошибка авторизации: ${error.error.message}`;
            }
            // Вариант 2: Формат с полем error
            else if (error.error.error) {
              errorMessage = `Ошибка авторизации: ${error.error.error}`;
            }
          }
          // Вариант 3: Строка в error
          else if (typeof error.error === 'string') {
            errorMessage = `Ошибка авторизации: ${error.error}`;
          }
        }

        // Всегда добавляем уведомление для ошибок 401
        notificationsService.addNotification({
          type: ENotificationCardType.ERROR,
          text: errorMessage,
          id: 'auth-error-' + Date.now().toString(36) + Math.random().toString(36).slice(2),
        });
      }
      // Проверяем формат ошибки для других случаев
      else if (
        error.status !== 200 &&
        error.error &&
        typeof error.error === 'object' &&
        error.error.message &&
        error.error.error &&
        error.error.statusCode
      ) {
        // Формируем текст уведомления
        let errorMessage = '';

        if (Array.isArray(error.error.message)) {
          errorMessage = error.error.message.join(', ');
        } else {
          errorMessage = String(error.error.message);
        }

        // Добавляем уведомление об ошибке
        notificationsService.addNotification({
          type: ENotificationCardType.ERROR,
          text: `${error.error.error}: ${errorMessage}`,
          id: 'error-' + Date.now().toString(36) + Math.random().toString(36).slice(2),
        });
      }

      // Здесь можно добавить дополнительную обработку если нужно

      // Пробрасываем ошибку дальше
      return throwError(() => error);
    }),
  );
};
